import { createSlice, createSelector } from '@reduxjs/toolkit'

import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'

export const MODES_REGLEMENT = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
  { value: 'prelevement', label: 'Prélèvement automatique' },
]

export const calcMontantTotal = ({montant_loyer, montant_charges, additional_lines = {}}) => {
  // Conversion des montants principaux avec gestion des erreurs
  const loyer = parseFloat(montant_loyer) || 0
  const charges = parseFloat(montant_charges) || 0
  
  // Initialisation des tableaux s'ils n'existent pas
  const rentLines = additional_lines.rent_lines || []
  const chargesLines = additional_lines.charges_lines || []
  
  // Calcul de la somme des montants des lignes additionnelles
  const rentLinesTotal = rentLines.reduce((sum, line) => sum + (parseFloat(line.montant) || 0), 0)
  const chargesLinesTotal = chargesLines.reduce((sum, line) => sum + (parseFloat(line.montant) || 0), 0)
  
  // Retourne la somme totale
  return loyer + charges + rentLinesTotal + chargesLinesTotal
}

// URL de l'API
const URL = 'quittances'

// États initiaux
const initialState = {
  items: [],
  bien: null,
  location: null,
  lateItems: [],
  loadingStatus: {
    fetchList: false,
    updateRent: false,
    updateBatchRents: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
  latePagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

// Thunks
export const fetchRents = createApiAsyncThunk(
  'rents/fetchList',
  async (params = {}, { api, dispatch, getState, rejectWithValue }) => {
    let url = URL
    if(params.location_id) {
      url = `locations/${params.location_id}/quittances`
    }
    delete params.location_id
    try {
      await setTimeoutInDev()
      const response = await api
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error('Error fetching rents:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des quittances')
    }
  }
)

export const generateRents = createApiAsyncThunk(
  'rents/generateQuittances',
  async (params, {api, dispatch, rejectWithValue}) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/generate`)
        .post()
      return response
    } catch(error) {
      console.error("Error generating rent receipts:", error)
      return rejectWithValue(error.message || "Une erreur est survenue lors de la génération des quittances.")
    }
  }
)

export const updateRent = createApiAsyncThunk(
  'rents/updateRent',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/${id}`)
        .put({ quittance: data })
      return response
    } catch (error) {
      console.error('Error updating rent receipt:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la mise à jour de la quittance')
    }
  }
)

export const updateBatchRents = createApiAsyncThunk(
  'rents/updateBatchRents',
  async ({ ids, data }, { api, dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/update_batch`)
        .post({ ids, quittance: data })
      return response
    } catch (error) {
      console.error('Error updating batch rent receipts:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la mise à jour des quittances')
    }
  }
)

export const deleteRent = createApiAsyncThunk(
  'rents/deleteRent',
  async (quittance, {api, dispatch, rejectWithValue}) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/${quittance.id}`)
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Le fichier de la quittance est supprimé."}))
      return response 
    } catch(error) {
      return rejectWithValue(error.data || {message: error.message, errors: {}})
    }
  }
)

// Slice
const rentsSlice = createSlice({
  name: 'rents',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
    setBien: (state, action) => {
      state.bien = action.payload
    },
    setLocation: (state, action) => {
      state.location = action.payload
    },
    setRentMontants: (state, action) => {
      const { id, montant_loyer, montant_charges } = action.payload
      const quittance = state.items.find(item => item.id === id) || state.lateItems.find(item => item.id === id)
      if (!quittance) return
      if(montant_loyer) {
        quittance.montant_loyer = montant_loyer
      }
      if(montant_charges) {
        quittance.montant_charges = montant_charges
      }
    },
    addRentLine: (state, action) => {
      const { quittanceId, description, montant } = action.payload
      const quittance = state.items.find(item => item.id === quittanceId) || state.lateItems.find(item => item.id === quittanceId)
      
      if (!quittance) return
      
      const additionalLines = quittance.additional_lines || { rent_lines: [], charges_lines: [] }
      const rentLines = additionalLines.rent_lines || []
      
      additionalLines.rent_lines = [
        ...rentLines,
        { description, montant: parseFloat(montant) }
      ]
      
      quittance.additional_lines = additionalLines
    },
    removeRentLine: (state, action) => {
      const { quittanceId, index } = action.payload
      const quittance = state.items.find(item => item.id === quittanceId) || state.lateItems.find(item => item.id === quittanceId)
      
      if (!quittance || !quittance.additional_lines || !quittance.additional_lines.rent_lines) return
      
      quittance.additional_lines.rent_lines = quittance.additional_lines.rent_lines.filter(
        (_, i) => i !== index
      )
    },
    addChargeLine: (state, action) => {
      const { quittanceId, description, montant } = action.payload
      const quittance = state.items.find(item => item.id === quittanceId) || state.lateItems.find(item => item.id === quittanceId)
      
      if (!quittance) return
      
      const additionalLines = quittance.additional_lines || { rent_lines: [], charges_lines: [] }
      const chargesLines = additionalLines.charges_lines || []
      
      additionalLines.charges_lines = [
        ...chargesLines,
        { description, montant: parseFloat(montant) }
      ]
      
      quittance.additional_lines = additionalLines
    },
    removeChargeLine: (state, action) => {
      const { quittanceId, index } = action.payload
      const quittance = state.items.find(item => item.id === quittanceId) || state.lateItems.find(item => item.id === quittanceId)
      
      if (!quittance || !quittance.additional_lines || !quittance.additional_lines.charges_lines) return
      
      quittance.additional_lines.charges_lines = quittance.additional_lines.charges_lines.filter(
        (_, i) => i !== index
      )
    },
  },
  extraReducers: (builder) => {
    // fetchRents
    builder.addCase(fetchRents.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchRents.fulfilled, (state, action) => {
      const { late } = action.meta?.arg || {}
      if(late) {
        state.lateItems = action.payload.quittances
        state.latePagination = {
          totalCount: action.payload.total_count,
          totalPages: action.payload.total_pages,
          currentPage: action.payload.current_page
        }
      } 
      else {
        state.items = action.payload?.quittances || []
        state.pagination = {
          totalCount: action.payload.total_count,
          totalPages: action.payload.total_pages,
          currentPage: action.payload.current_page
        }
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchRents.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })
    
    // Génération des Quittances
    builder.addCase(generateRents.pending, (state) => {
      state.loadingStatus.generateRents = true
      state.requestStatus.error = null
    })
    builder.addCase(generateRents.fulfilled, (state) => {
      state.loadingStatus.generateRents = false
      state.requestStatus.success = true
    })
    builder.addCase(generateRents.rejected, (state, action) => {
      state.loadingStatus.generateRents = false
      state.requestStatus.error = action.payload
    })
    
    // Mise à jour d'une quittance
    builder.addCase(updateRent.pending, (state) => {
      state.loadingStatus.updateRent = true
      state.requestStatus.error = null
    })
    builder.addCase(updateRent.fulfilled, (state, action) => {
      state.loadingStatus.updateRent = false
      state.requestStatus.success = true
      
      // Mettre à jour la quittance dans la liste
      let index = state.items.findIndex(item => item.id === action.payload.quittance.id)
      if (index !== -1) {
        state.items[index] = action.payload.quittance
      }
      else {
        index = state.lateItems.findIndex(item => item.id === action.payload.quittance.id)
        if (index !== -1) {
          state.lateItems[index] = action.payload.quittance
        }
      }
    })
    builder.addCase(updateRent.rejected, (state, action) => {
      state.loadingStatus.updateRent = false
      state.requestStatus.error = action.payload
    })
    
    // Mise à jour par lot des quittances
    builder.addCase(updateBatchRents.pending, (state) => {
      state.loadingStatus.updateBatchRents = true
      state.requestStatus.error = null
    })
    builder.addCase(updateBatchRents.fulfilled, (state, action) => {
      state.loadingStatus.updateBatchRents = false
      state.requestStatus.success = true
      
      action.payload.quittances.forEach( (quittance) => {
        // Mettre à jour la quittance dans la liste
        let index = state.items.findIndex(item => item.id === quittance.id)
        if (index !== -1) {
          state.items[index] = quittance
        }
        else {
          index = state.lateItems.findIndex(item => item.id === quittance.id)
          if (index !== -1) {
            state.lateItems[index] = quittance
          }
        }
      })
    })
    builder.addCase(updateBatchRents.rejected, (state, action) => {
      state.loadingStatus.updateBatchRents = false
      state.requestStatus.error = action.payload
    })

    // Suppression / Reset des quittances
    builder.addCase(deleteRent.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteRent.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id )
      if(index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteRent.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const {
  setFilters,
  setBien,
  setLocation,
  resetRequestStatus,
  setRentMontants,
  addRentLine,
  removeRentLine,
  addChargeLine,
  removeChargeLine
} = rentsSlice.actions

// Sélecteurs
export const selectNotPaidQuittances = createSelector(
  [(state) => state.quittances, (_, late) => late],
  (quittances, late) => {
    return (late ? quittances.lateItems : quittances.items).filter(quittance => !quittance.date_paiement)
  }
)
export const selectQuittancesByIds = createSelector(
  [(state) => state.quittances, (_, ids) => ids],
  (quittances, ids) => {
    if (!ids || !ids.length) return []
    return [
      ...quittances.items,
      ...quittances.lateItems
    ].filter(quittance => ids.includes(quittance.id))
  }
)
export const selectQuittanceById = createSelector(
  [(state) => state.quittances, (_, id) => id],
  (quittances, id) => {
    if (!id) return null
    return [
      ...quittances.items,
      ...quittances.lateItems
    ].find(quittance => quittance.id === id)
  }
)

export default rentsSlice.reducer